import { Button } from '@heroui/react';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import i18n from '~/common/helpers/i18n';
import { useTriggers } from '~/common/hooks/useTriggers';
import {
  ContentHeader,
  ContentHeaderActions,
  ContentHeaderControlGroup,
  ContentHeaderFilterSelect,
  ContentHeaderInput,
  ContentHeaderTitle,
  ContentHeaderViewToggle,
  Divider
} from '~/components/ContentHeader';
import Role from '~/components/Role';
import { useDeployUiStore } from '~/scenes/Runtime/scenes/Deployments/stores/deployUiStore';

type View = 'card' | 'table';

type HeaderProps = {
  /**
   * @deprecated view switching is handled by ContentHeaderViewToggle via URL state
   */
  onViewButtonClick?: (view: View) => void;

  /**
   * @deprecated filtering flows through ContentHeader URL params
   */
  search?: (e: React.ChangeEvent<HTMLInputElement>, tags: string[]) => void;
};

const tags = [
  {
    label: i18n.t('scenes.runtime.labels.deployed'),
    value: 'SERVICE_ACTIVE'
  },
  {
    label: i18n.t('scenes.runtime.labels.status_error'),
    value: 'SERVICE_ERROR'
  },
  {
    label: i18n.t('scenes.runtime.labels.redeploy'),
    value: 'REDEPLOY'
  },
  {
    label: i18n.t('scenes.runtime.labels.starting'),
    value: 'STARTING'
  }
];

const Header: FC<HeaderProps> = () => {
  const realm = useSelector((state: any) => state.application.activeRealm);
  const environment = useSelector(
    (state: any) => state.application.environment
  );
  const loading = useSelector((state: any) => state.project?.loading?.fetch);
  const setDeploySheetOpen = useDeployUiStore(
    state => state.setDeploySheetOpen
  );
  const triggersData = useTriggers(realm);
  const triggers = useMemo(
    () => [
      ...new Set(
        triggersData
          .map(trigger => trigger.jsonExample?.type)
          .filter((type): type is string => Boolean(type))
      )
    ],
    [triggersData]
  );

  return (
    <ContentHeader className='flex-col items-start gap-6' isLoaded={!loading}>
      <ContentHeaderTitle
        hasRefreshInterval
        title={i18n.t('scenes.runtime.labels.title')}
      />
      <div className='flex w-full items-end justify-between gap-4'>
        <ContentHeaderInput name='search' data-testid='run-input-search' />
        <ContentHeaderActions>
          <ContentHeaderControlGroup>
            <ContentHeaderFilterSelect
              label='Status'
              options={tags}
              filterKey='status'
              selectionMode='multiple'
              data-testid='run-status-select'
            />
            <ContentHeaderFilterSelect
              label='Triggers'
              options={triggers.map(trigger => ({
                value: trigger,
                label: trigger
              }))}
              filterKey='triggers'
              selectionMode='multiple'
              selectionLimit={5}
              data-testid='run-triggers-select'
            />
            <ContentHeaderViewToggle data-testid='run-view-toggle' />
          </ContentHeaderControlGroup>
          <Divider />
          <Role
            name={[
              'DEPLOYMENT:CREATE',
              `DEPLOYMENT:CREATE{ENV=${environment?.toUpperCase?.()}}`
            ]}
            environmentCapable
          >
            <Button
              variant='solid'
              color='primary'
              className='px-2'
              size='sm'
              onPress={() => setDeploySheetOpen(true)}
              data-testid='run-create-button'
            >
              {i18n.t('scenes.pipelines.actions.create_new')}
            </Button>
          </Role>
        </ContentHeaderActions>
      </div>
    </ContentHeader>
  );
};

export default Header;
